import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Spin, Input, Breadcrumb, Modal, Upload, message } from "antd";

import LoadingOverlay from "components/loading-overlay/loading-overlay";
import HandoverDeviceDetectionSummary from "components/handover-device-detection-summary/handover-device-detection-summary";
import HandoverDeviceDetectionResults from "components/handover-device-detection-results/handover-device-detection-results";
import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";

import { DeviceDetectionJobStatus } from "constants/field-values";
import routeNames from "constants/route-names";
import { getFileIcon, createBeforeUpload } from "utils/files-management";
import { useDeviceDetectionCore } from "hooks/useDeviceDetectionCore";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useDeleteDeviceDetectionJobByIdMutation } from "graphql/mutations/delete-device-detection-job.generated";
import { useGetHandoverByIdQuery } from "graphql/queries/get-handover-by-id.generated";
import { Properties } from "graphql/generated/graphql";

/**
 * HandoverDeviceDetection — Handover context screen.
 *
 * Lives under LoggedNavigationLayout at:
 *   HANDOVER_DEVICE_DETECTION_ADD     /handover-device-detection/add
 *   HANDOVER_DEVICE_DETECTION_DETAILS /handover-device-detection/:jobId
 *
 * Property is pre-selected from the handover and cannot be changed.
 */
const HandoverDeviceDetection: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	// Required: both params must be present when navigating from HandoverDetails
	const handoverId = searchParams.get("handoverId") ?? undefined;
	const propertyId = searchParams.get("propertyId") ?? undefined;

	const handleOnBack = () => {
		if (handoverId) {
			navigate(`${routeNames.HANDOVERS}/${handoverId}`);
		} else {
			navigate(routeNames.HANDOVERS);
		}
	};

	const handleOnJobReady = (jobId: string) => {
		// Stay in handover layout; carry the handover/property params forward
		const params = new URLSearchParams();
		if (handoverId) params.set("handoverId", handoverId);
		if (propertyId) params.set("propertyId", propertyId);
		navigate(`${routeNames.HANDOVER_DEVICE_DETECTION_DETAILS.replace(":jobId", jobId)}?${params.toString()}`);
	};

	const { isUserAnon: isAnon } = useLoggedUser();

	// cache-first: el usuario viene de HandoverDetails que ya fetcheó este handover,
	// así que esta query resuelve del caché sin network request.
	const { data: handoverData } = useGetHandoverByIdQuery({
		variables: { id: handoverId!, isAnon },
		skip: !handoverId,
		fetchPolicy: "cache-and-network"
	});
	const handoverProperty = handoverData?.handover_by_pk?.property;

	// Guard: only one job allowed per handover at a time.
	// Catches re-entry from a second tab or navigating back and resubmitting.
	const existingJobs = handoverData?.handover_by_pk?.device_detection_jobs ?? [];

	// Only for upload form and summary header
	const core = useDeviceDetectionCore({
		onBack: handleOnBack,
		onJobReady: handleOnJobReady,
		handoverId,
		propertyId,
		initialProperty: (handoverProperty as Properties) ?? null
	});

	const {
		attachmentsFiles,
		setAttachmentsFiles,
		setJobId,
		setProcessingStarted,
		setAddAttachmentModalOpen,
		addAttachmentModalOpen,
		addAttachmentModalEditMode,
		handleOnCloseAttachmentModal,
		updateAttachments,
		vendorDeviceNotes,
		setVendorDeviceNotes,
		selectedProperty,
		isLoading,
		uploadingDetectionFiles,
		processingStarted,
		job,
		processingData,
		handleOnRemoveAttachment,
		handleOnSubmit,
		publishDetection,
		allInstalledProductsSkipped
	} = core;

	const handleSubmitGuarded = () => {
		if (existingJobs.length > 0) {
			message.warning("This handover already has a detection job in progress. Cancel it before starting a new one.");
			return;
		}
		handleOnSubmit();
	};

	// Delete detection job mutation
	const [deleteDetectionJobMutation, { loading: loadingDelete }] = useDeleteDeviceDetectionJobByIdMutation({
		onCompleted: () => {
			setProcessingStarted(false);
			setJobId(null);
			setAttachmentsFiles([]);
		},
		onError: error => {
			console.error("Error deleting detection job:", error);
		}
	});

	const handleCancelDetection = () => {
		Modal.confirm({
			title: "Start over with a fresh detection?",
			content:
				"This'll delete everything we've detected so far. You'll be able to upload new files and start from scratch. Sound good?",
			okText: "Yes, start over",
			cancelText: "Never mind",
			onOk: async () => {
				if (job?.id) {
					await deleteDetectionJobMutation({ variables: { id: job.id } });
				}
			}
		});
	};

	return (
		<div className="hdd-container">

			<Breadcrumb
				className="products-breadcrumbs"
				items={[{ title: "Complete Handover", onClick: handleOnBack }, { title: "Devices and Equipment" }]}
			/>

			<div className="hdd-content-container">
				{!processingStarted ? (
					// ── Upload form ──
					<form className="hdd-upload-form">
						<p className="hdd-description">
							Upload documentation of all devices and components installed as part of this project. Include photos,
							videos, or documents (e.g. field notes, work orders, invoices) that contain specific details such as make,
							model, serial numbers, and installation locations. Our AI will handle the rest, and you'll have a chance
							to review everything before submitting this Handover for customer review.
						</p>

						{/* Drop zone — click opens modal, drag-and-drop adds files directly */}
						<Upload.Dragger
							className="hdd-dropzone-dragger"
							multiple
							showUploadList={false}
							disabled={isLoading}
							openFileDialogOnClick={false}
							beforeUpload={async (file, fileList) => {
								const beforeUploadFn = createBeforeUpload();
								
								let result;
								if (beforeUploadFn) {
									result = await beforeUploadFn(file, fileList);
								}
								
								if (result === Upload.LIST_IGNORE) {
									return Upload.LIST_IGNORE;
								}
								
								const finalFile = (result as any) || file;

								// Dropped files are added directly to the attachments list
								const droppedFile = {
									uid: finalFile.uid || file.uid,
									name: finalFile.name,
									file: finalFile,
									type: finalFile.type?.split("/")[0] || "document",
									mime_type: finalFile.type,
									originFileObj: finalFile
								};
								setAttachmentsFiles(prev => [...prev, droppedFile]);
								return false; // prevent Ant's auto-upload
							}}
						>
							<div
								className="hdd-dropzone-inner"
								onClick={e => {
									// Click opens the AddAttachmentModal (with metadata fields)
									e.stopPropagation();
									if (!isLoading) setAddAttachmentModalOpen(true);
								}}
							>
								<img
									src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle-purple.svg`}
									alt="Add files"
									className="hdd-dropzone-icon"
								/>
							</div>
						</Upload.Dragger>

						{/* File grid */}
						{attachmentsFiles.length > 0 && (
							<div className="hdd-files-grid">
								{attachmentsFiles.map(attachmentFile => {
									const uid = attachmentFile.uid || attachmentFile.id;
									const FileIcon = getFileIcon(attachmentFile.file?.type || attachmentFile.mime_type || "document");
									return (
										<div key={`hdd-attachment-${uid}`} className="hdd-file-item">
											<div className="hdd-file-name">
												<FileIcon className="hdd-file-icon" />
												<span className="hdd-file-label">{attachmentFile.name}</span>
											</div>
											<button
												className="hdd-file-remove"
												disabled={isLoading}
												onClick={() => !isLoading && handleOnRemoveAttachment(attachmentFile)}
												aria-label="Remove file"
											>
												<img
													src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash-purple.svg`}
													alt="Remove"
													className="hdd-file-trash"
												/>
											</button>
										</div>
									);
								})}
							</div>
						)}

						{/* Additional notes */}
						<div className="hdd-notes-field">
							<span className="hdd-notes-label">Additional notes (optional)</span>
							<Input.TextArea
								className="hdd-notes-input"
								value={vendorDeviceNotes}
								onChange={e => setVendorDeviceNotes(e.target.value)}
								placeholder="e.g. We also found two damaged cameras outside the gazebo and replaced those while on-site."
								autoSize={{ minRows: 2 }}
							/>
						</div>

						<Button
							className="device-detection-button"
							onClick={handleSubmitGuarded}
							disabled={isLoading || !selectedProperty || attachmentsFiles.length === 0}
						>
							Start automagical detection
						</Button>
					</form>
				) : (
					// ── Processing / results ──
					<div className="device-detection-processing-container">
						{job?.status === DeviceDetectionJobStatus.DRAFT || job?.status === DeviceDetectionJobStatus.PUBLISHED ? (
							<div className="device-detection-results-container">
								{/* Title + action buttons */}
								<div className="hdd-summary-header-row">
									<span className="hdd-page-title">Project Summary</span>
									<div className="hdd-summary-header-row-buttons">
										{job?.status === DeviceDetectionJobStatus.DRAFT && (
											<button
												className="ha-cancel-btn"
												onClick={handleCancelDetection}
												disabled={isLoading || loadingDelete}
											>
												Cancel
											</button>
										)}
										<Button
											className="device-detection-button"
											onClick={publishDetection}
											loading={isLoading}
											disabled={isLoading || loadingDelete || job?.status === DeviceDetectionJobStatus.PUBLISHED}
										>
											{job?.status === DeviceDetectionJobStatus.DRAFT ? "Save and continue" : "Published"}
										</Button>
									</div>
								</div>

								<HandoverDeviceDetectionSummary
									aiResponse={job?.ai_response}
									detectedInstalledProducts={job?.detected_installed_products}
									productsGroupedByType={job.products_grouped_by_type}
									isLoading={isLoading}
									allInstalledProductsSkipped={allInstalledProductsSkipped}
								/>

								<HandoverDeviceDetectionResults deviceDetectionCore={core} />
							</div>
						) : job?.status === DeviceDetectionJobStatus.FAILED ? (
							<Card className="device-detection-error-card">
								<h2>Detection Failed</h2>
								<p>An error occurred during device detection</p>
								<Button
									className="device-detection-retry-button"
									onClick={() => {
										setProcessingStarted(false);
										setJobId(null);
										setAttachmentsFiles([]);
									}}
								>
									Try Again
								</Button>
							</Card>
						) : (
							// Processing spinner — handover-specific styling
							<div className="hdd-processing-card">
								{(processingData?.status === DeviceDetectionJobStatus.PENDING ||
									processingData?.status === DeviceDetectionJobStatus.PROCESSING) && (
									<>
										<Spin />
										<p>{"Grab a drink while our AI does its thing. We can't have you getting dehydrated..."}</p>
									</>
								)}
							</div>
						)}
					</div>
				)}
			</div>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
				filesLimit={20}
			/>

			{/* ── Upload form modal ── */}
			<LoadingOverlay isLoading={isLoading} tip={uploadingDetectionFiles ? "Uploading your files…" : ""} />
		</div>
	);
};

export default HandoverDeviceDetection;
